import { useState, useEffect } from "react";
import { Form, Button, Grid } from "semantic-ui-react";
import { useFormik } from "formik";
import { Notification, Level, Grade } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { withEmptyOption } from "../../../../utils";
import { showSuccess, showError } from "../../../../utils/sweetalert";
import { initialValues, validationSchema } from "./NotificationForm.form";

const notificationController = new Notification();
const levelController = new Level();
const gradeController = new Grade();

const typeOptions = [
  { key: "general", text: "General", value: "general" },
  { key: "event", text: "Evento", value: "event" },
  { key: "course", text: "Curso", value: "course" },
  { key: "payment", text: "Pago", value: "payment" },
  { key: "grade", text: "Calificación", value: "grade" },
  { key: "forum", text: "Foro", value: "forum" },
];

const modeOptions = [
  { key: "all", text: "Todos los estudiantes", value: "all" },
  { key: "level", text: "Por nivel", value: "level" },
  { key: "grade", text: "Por grado", value: "grade" },
];

export function NotificationForm(props) {
  const { onClose, onReload } = props;
  const { accessToken } = useAuth();
  const [levels, setLevels] = useState([]);
  const [grades, setGrades] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [levelsRes, gradesRes] = await Promise.all([
          levelController.getLevels(accessToken),
          gradeController.getGrades(accessToken),
        ]);

        setLevels(
          (levelsRes.data || levelsRes).map((level) => ({
            key: level.id,
            text: level.name,
            value: level.id,
          }))
        );

        setGrades(gradesRes.data || gradesRes);
      } catch (error) {
        console.error("Error loading form data:", error);
      }
    })();
  }, [accessToken]);

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const result = await notificationController.broadcastNotification(
          formValue
        );
        showSuccess("Notificación enviada", result.msg);
        onReload();
        onClose();
      } catch (error) {
        showError(
          "No se pudo enviar la notificación",
          error?.msg || "Ocurrió un error inesperado."
        );
      }
    },
  });

  return (
    <Form className="notification-form" onSubmit={formik.handleSubmit}>
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <Form.Input
              name="title"
              placeholder="Título de la notificación *"
              label="Título *"
              onChange={formik.handleChange}
              value={formik.values.title}
              error={formik.errors.title}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <Form.TextArea
              name="message"
              placeholder="Mensaje de la notificación *"
              label="Mensaje *"
              onChange={formik.handleChange}
              value={formik.values.message}
              error={formik.errors.message}
              rows={4}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="type"
              placeholder="Tipo *"
              label="Tipo *"
              options={typeOptions}
              onChange={(e, data) => formik.setFieldValue("type", data.value)}
              value={formik.values.type}
              error={formik.errors.type}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="mode"
              placeholder="Notificar a *"
              label="Notificar a *"
              options={modeOptions}
              onChange={(e, data) => {
                formik.setFieldValue("mode", data.value);
                formik.setFieldValue("levelId", null);
                formik.setFieldValue("gradeId", null);
              }}
              value={formik.values.mode}
              error={formik.errors.mode}
            />
          </Grid.Column>
        </Grid.Row>

        {(formik.values.mode === "level" || formik.values.mode === "grade") && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Select
                className="edunova-select"
                name="levelId"
                placeholder="Seleccione un nivel *"
                label="Nivel *"
                clearable
                options={withEmptyOption(levels, "Seleccione un nivel *")}
                onChange={(e, data) => {
                  formik.setFieldValue("levelId", data.value);
                  if (formik.values.mode === "grade") {
                    formik.setFieldValue("gradeId", null);
                  }
                }}
                value={formik.values.levelId}
                error={formik.errors.levelId}
              />
            </Grid.Column>
          </Grid.Row>
        )}

        {formik.values.mode === "grade" && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Select
                className="edunova-select"
                name="gradeId"
                placeholder="Seleccione un grado *"
                label="Grado *"
                clearable
                disabled={!formik.values.levelId}
                options={withEmptyOption(
                  grades
                    .filter((grade) => grade.levelId === formik.values.levelId)
                    .map((grade) => ({
                      key: grade.id,
                      text: grade.name,
                      value: grade.id,
                    })),
                  "Seleccione un grado *"
                )}
                onChange={(e, data) =>
                  formik.setFieldValue("gradeId", data.value)
                }
                value={formik.values.gradeId}
                error={formik.errors.gradeId}
              />
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row>
          <Grid.Column width={16}>
            <Button type="submit" primary fluid loading={formik.isSubmitting}>
              Enviar notificación
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Form>
  );
}
